<template>
  <div
    class="h-full space-y-4 flex flex-col justify-between bg-white overflow-hidden"
  >
    <agent-view
      open
      isEmbedded
      hideHeader
      autofocus
      ref="agentViewRef"
      hostApp="copilot"
      :defaultAgentId="defaultAgentId"
      :locale="locale"
      :username="username"
      :appContext="agentContext"
      :logContext="logContext"
      :loading="copilotLoading"
      :hideInput="shouldDisableInput"
      :scopeOptions="scopeOptions"
      :filtersOptions="agentFilters"
      @go-to-content="emit('go-to-content', $event)"
      @action="emit('action', $event)"
    >
      <template #session-start>
        <div
          class="space-y-3 flex flex-col my-2 w-full"
          v-if="shouldDisplayTicketContent || shouldDisplayError"
        >
          <div
            v-if="shouldDisplayTicketContent"
            class="p-2 shadow-sm rounded-lg space-y-2"
            :style="backgroundGradient"
          >
            <CopilotContext
              :sentiment="ticketContext?.sentiment"
              :context="ticketContext?.user_request"
              :loading="ticketContextLoading"
              :displaySentiment="copilotToolParameters?.displayMoodAnalysis"
              :displayContext="copilotToolParameters?.displayTicketSummary"
            />
            <CopilotAutomations
              :manual-automations-loading="manualAutomationsLoading"
              :manual-automations="manualAutomations"
              :brain-automations-loading="brainAutomationsLoading"
              :brain-automations="brainAutomations"
              :lang="selectedLanguage || locale"
              :displayManualRecommendations="
                copilotToolParameters?.displayManualRecommendations
              "
              :displayBrainRecommendations="
                copilotToolParameters?.displayBrainRecommendations
              "
              @go-to-content="
                emit('go-to-content', { ...$event, automation: true })
              "
            />
            <BaseAiButton
              v-if="shouldDisplayGenerateAnswerCTA"
              size="xs"
              :text="$t('genius.generate-answer.cta')"
              :disabled="copilotLoading"
              @click="handleGenerateAnswer($t('genius.generate-answer.cta'))"
            />
          </div>
          <div
            v-if="shouldDisplayError"
            class="w-full bg-red-1 rounded-lg py-2 px-3 text-body space-y-2 my-1"
          >
            <div class="flex w-full space-x-3 items-start text-sm">
              <FontAwesomeIcon :icon="['fas', 'circle-xmark']" class="mt-0.5" />
              <div class="space-y-2">
                <div class="font-bold">
                  {{ $t('genius.error.title') }}
                </div>
                <div class="text-gray-6 text-xs">
                  {{ $t('genius.error.description') }}
                </div>
              </div>
            </div>
            <div class="w-full flex justify-end space-x-2">
              <BaseButton
                variant="tertiary"
                size="sm"
                :text="$t('genius.error.cancel')"
                @click="goToHome"
              />
              <BaseButton
                variant="secondary"
                size="sm"
                :text="$t('genius.error.reload')"
                @click="handleFetchRecommendations"
              />
            </div>
          </div>
        </div>
      </template>
      <template #input-header>
        <div class="py-2 space-y-2">
          <BaseMessageBox
            v-if="testModeChanged"
            multiline
            variant="info"
            :title="$t('genius.ticket-warning.test-mode.title')"
            :message="$t('genius.ticket-warning.test-mode.description')"
            @close="testModeChanged = false"
          />
          <BaseMessageBox
            v-if="newTicket"
            multiline
            variant="warning"
            :title="$t('genius.ticket-warning.new-ticket.title')"
            :message="$t('genius.ticket-warning.new-ticket.description')"
            :actions="warningMessagesActions.new"
            :showClose="false"
          />
          <BaseMessageBox
            v-else-if="ticketLeft && !!currentAgentCopilotTool"
            multiline
            variant="warning"
            :title="$t('genius.ticket-warning.no-ticket.title')"
            :message="$t('genius.ticket-warning.no-ticket.description')"
            :actions="warningMessagesActions.empty"
            :showClose="false"
          />
          <BaseMessageBox
            v-else-if="ticketChanged && !!currentAgentCopilotTool"
            multiline
            variant="warning"
            :title="$t('genius.ticket-warning.change-ticket.title')"
            :message="$t('genius.ticket-warning.change-ticket.description')"
            :actions="warningMessagesActions.change"
            :showClose="false"
          />
        </div>
      </template>
    </agent-view>
  </div>
</template>

<script setup lang="ts">
import { sentimentsList } from '../../utils/sentimentsList';
import {
  useCopilotStore,
  useAdminStore,
  useKnowledgeStore,
  ExternalContent,
} from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import CopilotContext from './CopilotContext.vue';
import CopilotAutomations from './CopilotAutomations.vue';
import type { LightEntity } from '@getmayday/vue-modules';

const copilotStore = useCopilotStore();
const {
  copilotLoading,
  manualAutomations,
  manualAutomationsLoading,
  brainAutomations,
  brainAutomationsLoading,
  ticketContext,
  ticketContextLoading,
  focusedTicket,
  copilotErrored,
  copilotHasData,
} = storeToRefs(copilotStore);

const configStore = useConfigStore();
const { currentTabContext, locale, geniusTestMode } = storeToRefs(configStore);

const adminStore = useAdminStore();
const { username, hasCompanyBrainAgentsPreference } = storeToRefs(adminStore);

const agentStore = useAgentStore();
const {
  currentSession,
  currentAgent,
  availableAgents,
  currentAgentCopilotTool,
  historyConversationId,
  getAgentsWithTool,
} = storeToRefs(agentStore);

const knowledgeStore = useKnowledgeStore();
const { selectedLanguage, path, content, focusKnowledge, modalContent } =
  storeToRefs(knowledgeStore);

const { defaultAgentId } = useCopilot();

const router = useRouter();

const { t } = useI18n();

const emit = defineEmits<{
  (event: 'action', payload: any): void;
  (event: 'go-to-content', payload: any): void;
  (event: 'get-recommendations'): void;
}>();

const ticketLeft = ref(false);
const newTicket = ref(false);
const ticketChanged = ref(false);
const testModeChanged = ref(false);

const agentViewRef = ref(null as any | null);
const labels: Ref<Label[]> = ref([]);

onMounted(async () => {
  labels.value = await knowledgeStore.getAllContentLabels();
});

const warningMessagesActions = computed(() => ({
  change: [
    {
      text: t('genius.ticket-warning.change-ticket.ignore'),
      variant: 'tertiary',
      onClick: () => {
        ticketChanged.value = false;
      },
    },
    {
      text: t('genius.ticket-warning.change-ticket.analyse'),
      variant: 'secondary',
      onClick: handleFetchRecommendations,
    },
  ],
  empty: [
    {
      text: t('genius.ticket-warning.no-ticket.confirm'),
      variant: 'tertiary',
      onClick: () => {
        ticketLeft.value = false;
      },
    },
    {
      text: t('genius.ticket-warning.no-ticket.new-conversation'),
      variant: 'secondary',
      onClick: handleNewConversation,
    },
  ],
  new: [
    {
      text: t('genius.ticket-warning.new-ticket.ignore'),
      variant: 'tertiary',
      onClick: () => {
        newTicket.value = false;
      },
    },
    {
      text: t('genius.ticket-warning.new-ticket.analyse'),
      variant: 'secondary',
      onClick: handleFetchRecommendations,
    },
  ],
}));

const backgroundGradient = computed(() => {
  let color = '#FFFFFF';
  if (!ticketContextLoading || ticketContext.value?.sentiment) {
    const sentimentColor = sentimentsList(
      ticketContext.value.sentiment - 1,
    ).color;
    if (sentimentColor) {
      color = sentimentColor;
    }
  }
  const bigint = parseInt(color.split('#')[1], 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return {
    transition: 'background 0.5s ease',
    background: `radial-gradient(ellipse farthest-side at top right, rgba(${r},${g},${b}, 1) 20%, white 50%)`,
    backgroundPosition: 'top left',
  };
});

const hasUserAccessToCopilotAgent = computed(() => {
  return getAgentsWithTool.value('copilot')?.length;
});

const isTicketFocused = computed(
  () => !!currentTabContext.value?.conversationId,
);

const hasConversationStarted = computed(
  () => !!currentSession.value?.conversations?.length,
);

const agentFilters = computed(() => {
  return {
    labels: labels.value,
  };
});

const shouldDisplayError = computed(() => {
  return (
    !copilotLoading.value &&
    copilotErrored.value &&
    !copilotHasData.value &&
    isTicketFocused.value &&
    !!currentAgentCopilotTool.value
  );
});

const shouldDisableInput = computed(() => {
  return (
    !hasCompanyBrainAgentsPreference.value ||
    shouldDisplayError.value ||
    !availableAgents.value?.length
  );
});

const shouldDisplayTicketContent = computed(() => {
  return (
    !!currentAgentCopilotTool.value &&
    (copilotLoading.value || copilotHasData.value) &&
    !shouldHideCopilot.value &&
    !nothingToDisplay.value
  );
});

const shouldDisplayGenerateAnswerCTA = computed(() => {
  return (
    hasCompanyBrainAgentsPreference.value &&
    !hasConversationStarted.value &&
    !shouldDisplayError.value &&
    isTicketFocused.value &&
    copilotToolParameters.value.displayGenerateAnswerCta
  );
});
const copilotToolParameters = computed(() => {
  const toolArguments = currentAgentCopilotTool.value?.arguments;
  if (!toolArguments)
    return {
      displayMoodAnalysis: true,
      displayManualRecommendations: true,
      displayBrainRecommendations: true,
      displayTicketSummary: true,
      displayGenerateAnswerCta: true,
    };
  const {
    display_mood_analysis,
    display_manual_recommendations,
    display_brain_recommendations,
    display_ticket_summary,
    display_generate_answer_cta,
  } = toolArguments;

  return {
    displayMoodAnalysis:
      display_mood_analysis?.isChecked !== undefined
        ? display_mood_analysis.isChecked
        : true,
    displayManualRecommendations:
      display_manual_recommendations?.isChecked !== undefined
        ? display_manual_recommendations.isChecked
        : true,
    displayBrainRecommendations:
      display_brain_recommendations?.isChecked !== undefined
        ? display_brain_recommendations.isChecked
        : true,
    displayTicketSummary:
      display_ticket_summary?.isChecked !== undefined
        ? display_ticket_summary.isChecked
        : true,
    displayGenerateAnswerCta:
      display_generate_answer_cta?.isChecked !== undefined
        ? display_generate_answer_cta.isChecked
        : true,
  };
});

const shouldHideCopilot = computed(() => {
  return Object.keys(copilotToolParameters.value).every(
    (key) =>
      copilotToolParameters.value![
        key as keyof typeof copilotToolParameters.value
      ] === false,
  );
});

const nothingToDisplay = computed(() => {
  if (copilotLoading.value) return false;
  if (
    copilotToolParameters.value.displayMoodAnalysis &&
    ticketContext?.value.sentiment
  )
    return false;
  if (
    copilotToolParameters.value.displayTicketSummary &&
    ticketContext?.value.user_request
  )
    return false;
  if (
    copilotToolParameters.value.displayManualRecommendations &&
    manualAutomations.value.length
  )
    return false;
  if (
    copilotToolParameters.value.displayBrainRecommendations &&
    brainAutomations.value.length
  )
    return false;
  return true;
});

const agentContext = computed(() => {
  return {
    knowledgeId: focusKnowledge.value?.id,
    language: selectedLanguage.value,
    ticketId: focusedTicket.value,
    helpdesk: currentTabContext.value?.tabSource,
    enrichments: ticketContext.value,
    ticketFocused: isTicketFocused.value,
    automations: [...brainAutomations.value, ...manualAutomations.value].map(
      (automation: LightEntity | ExternalContent) => ({
        entityId: automation.id,
        entityType: automation.hasOwnProperty('type')
          ? 'attached-contents'
          : 'external-contents',
      }),
    ),
    ...(isTicketFocused.value
      ? {}
      : {
          disabledAgents: {
            'mayday:copilot': t('genius.disabled-agent.copilot'),
          },
        }),
    currentPath: path.value.reverse(),
  };
});

const logContext = computed(() => {
  return {
    trigger: 'tab',
    platform: 'zendesk',
  };
});

const handleFetchRecommendations = async () => {
  newTicket.value = false;
  ticketLeft.value = false;
  ticketChanged.value = false;
  emit('get-recommendations');
};

const handleGenerateAnswer = (input: string) => {
  if (!agentViewRef) return;
  agentViewRef.value.sendMessage(input);
};

const handleNewConversation = async () => {
  if (!agentViewRef.value) return;
  ticketLeft.value = false;
  newTicket.value = false;
  ticketChanged.value = false;
  copilotStore.reset();
  if (
    !currentTabContext?.value?.conversationId &&
    currentAgent.value?.id === 'mayday:copilot'
  )
    agentStore.setCurrentAgent(null);
  await agentViewRef.value.newSession();
};

const toggleSessionsPanel = async () => {
  if (!agentViewRef.value) return;
  await agentViewRef.value.toggleSessionsPanel();
};

const goToHome = () => {
  router.push({ path: '/' });
};

watch(
  () => currentTabContext.value?.conversationId,
  (newVal, oldVal) => {
    ticketChanged.value = false;
    newTicket.value = false;
    ticketLeft.value = false;
    // Check if ticket is left
    if (
      !newVal &&
      !!oldVal &&
      !!currentAgentCopilotTool.value &&
      copilotHasData.value
    ) {
      return (ticketLeft.value = true);
    }
    // Check if new ticket
    if (
      newVal &&
      (!oldVal || !!!currentAgentCopilotTool.value) &&
      hasConversationStarted.value &&
      hasUserAccessToCopilotAgent.value
    ) {
      return (newTicket.value = true);
    }
    // Check if ticket changed
    if (
      newVal &&
      oldVal &&
      newVal !== focusedTicket.value &&
      (copilotHasData.value || copilotLoading.value)
    ) {
      return (ticketChanged.value = true);
    }
  },
);

watch(
  () => copilotLoading.value,
  () => {
    copilotStore.updateHasUserOpenedCopilot(true);
  },
  { immediate: true },
);

watch(
  () => geniusTestMode.value,
  (newVal, oldVal) => {
    testModeChanged.value = !!newVal && newVal !== oldVal;
  },
);

watch(
  () => currentAgentCopilotTool.value,
  (newVal) => {
    if (!newVal || historyConversationId.value) {
      return copilotStore.reset();
    }
    handleFetchRecommendations();
  },
);

watch(historyConversationId, (newVal) => {
  if (!newVal) return;
  copilotStore.reset();
});

// Scope Select
interface scopeOption {
  id: string;
  key: string;
  label: string;
}
const scopeOptions = ref<{
  Knowledge: scopeOption | null;
  Case: scopeOption | null;
  Content: scopeOption | null;
}>({ Knowledge: null, Case: null, Content: null });

const findEntity = (key: string, path: LightEntity[]) => {
  const entity = path.find((entity) => entity.type === key);
  if (!entity) return null;
  return { id: entity.id, key, label: entity.label };
};

const optKnowledge = computed(() => {
  if (!focusKnowledge.value) return null;
  const { id, label } = focusKnowledge.value;
  return { id, key: 'Knowledge', label };
});

const optCase = computed(() => findEntity('Case', [...path.value].reverse()));

const optContent = computed(() => {
  const focus =
    (content.value.type && content.value.type !== 'Case' && content) ||
    (modalContent.value.type &&
      modalContent.value.type !== 'Answer' &&
      modalContent);

  if (!focus) return null;

  const { id, label } = focus.value;
  return { id, key: 'Content', label };
});

onBeforeMount(() => {
  scopeOptions.value = {
    Knowledge: optKnowledge.value,
    Case: optCase.value,
    Content: optContent.value,
  };
});

defineExpose({
  handleNewConversation,
  toggleSessionsPanel,
});
</script>

<style scoped></style>
